import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Paper, ScrollArea, Center, Loader, Tabs, Group, Text, ActionIcon, Menu, useMantineTheme, ThemeIcon, Tooltip, Stack } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronDown, IconClipboardList, IconMailOpened } from "@tabler/icons-react";

import { ListWorkOrderDto, WorkOrderStatus } from "../../models/workorder.models";
import { useMarkAllWorkOrdersReadMutation } from "../../api/workorder.api";
import { WorkOrderListItem } from "./WorkOrderListItem";
import { SortOptions, useSortedWorkOrders } from "../../functions/workorder-sort.hooks";
import { useWorkOrderFilters, useFilteredWorkOrders } from "../../functions/workorder-filter.hooks";
import { useSearch } from "../../functions/SearchBox.hook";
import { NoSearchResults } from "../Shared/NoSearchResults";
import { NoFilterResults } from "../Shared/NoFilterResults";
import { NoDataResults } from "../Shared/NoDataResults";
import classes from './WorkOrderList.module.css';

interface WorkOrderListProps {
	workOrders?: ListWorkOrderDto[];
	isLoading?: boolean;
	selectedWorkOrder?: ListWorkOrderDto | null;
	setSelectedWorkOrder?: (workOrder: ListWorkOrderDto | null) => void;
}
export const WorkOrderList = ({ workOrders, isLoading, selectedWorkOrder, setSelectedWorkOrder }: WorkOrderListProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const params = useParams<{ workOrderId?: string }>();
	// const workOrderIdFromParams = params.workOrderId || null;
	const isNewRequest = params.workOrderId === 'new-request';
	const isNewWorkOrder = params.workOrderId === 'new';
	const { query, setQuery } = useSearch();
	const { statusFilter, resetFilters } = useWorkOrderFilters();
	const { mutate: markAllAsRead, isPending: processingMarkAllAsRead } = useMarkAllWorkOrdersReadMutation();

	// const { data: workOrders, isLoading } = useGetWorkOrdersQuery();
	const [selectedTab, setSelectedTab] = useState<string | null>(isNewWorkOrder ? 'todo' : 'requested');
	const [preserveSelectedTab, setPreserveSelectedTab] = useState<boolean>(false);

	const workOrdersRequested = workOrders?.filter(wo => wo.Status === WorkOrderStatus.Requested && wo.Deleted == null) || [];
	const { filteredWorkOrders: workOrdersFilteredRequested, filterNoResults: filterNoResultsRequested, searchNoResults: searchNoResultsRequested, noData: noDataRequested } = useFilteredWorkOrders(workOrdersRequested);
	const { sortedList: requestedWorkOrders, SortOptionsDropdown: RequestedSortOptionsDropdown } = useSortedWorkOrders(workOrdersFilteredRequested, 'requested', [SortOptions.Created]);

	const workOrdersTodo = workOrders?.filter(wo => wo.Status != WorkOrderStatus.Completed && wo.Status != WorkOrderStatus.Archived && wo.Status != WorkOrderStatus.Requested && wo.Deleted == null) || [];
	const { filteredWorkOrders: workOrdersFilteredTodo, filterNoResults: filterNoResultsTodo, searchNoResults: searchNoResultsTodo, noData: noDataTodo } = useFilteredWorkOrders(workOrdersTodo);
	const { sortedList: todoWorkOrders, SortOptionsDropdown: ToDoSortOptionsDropdown } = useSortedWorkOrders(workOrdersFilteredTodo, 'todo', [SortOptions.Created, SortOptions.Unread, SortOptions.Updated, SortOptions.DueDate, SortOptions.Priority, SortOptions.Status, SortOptions.WorkOrderNumber]);

	const workOrdersDone = workOrders?.filter(wo => wo.Status === WorkOrderStatus.Completed && wo.Deleted == null) || [];
	const { filteredWorkOrders: workOrdersFilteredDone, filterNoResults: filterNoResultsDone, searchNoResults: searchNoResultsDone, noData: noDataDone } = useFilteredWorkOrders(workOrdersDone);
	const { sortedList: doneWorkOrders, SortOptionsDropdown: DoneSortOptionsDropdown } = useSortedWorkOrders(workOrdersFilteredDone, 'done', [SortOptions.Completed, SortOptions.WorkOrderNumber]);

	const workOrdersArchived = workOrders?.filter(wo => wo.Status === WorkOrderStatus.Archived && wo.Deleted == null) || [];
	const { filteredWorkOrders: workOrdersFilteredArchived, filterNoResults: filterNoResultsArchived, searchNoResults: searchNoResultsArchived, noData: noDataArchived } = useFilteredWorkOrders(workOrdersArchived);
	const { sortedList: archivedWorkOrders, SortOptionsDropdown: ArchivedSortOptionsDropdown } = useSortedWorkOrders(workOrdersFilteredArchived, 'archived', [SortOptions.Completed, SortOptions.WorkOrderNumber]);

	const workOrdersDeleted = workOrders?.filter(wo => wo.Deleted != null) || [];
	const { filteredWorkOrders: workOrdersFilteredDeleted, filterNoResults: filterNoResultsDeleted, searchNoResults: searchNoResultsDeleted, noData: noDataDeleted } = useFilteredWorkOrders(workOrdersDeleted);
	const { sortedList: deletedWorkOrders, SortOptionsDropdown: DeletedSortOptionsDropdown } = useSortedWorkOrders(workOrdersFilteredDeleted, 'deleted', [SortOptions.Deleted, SortOptions.WorkOrderNumber]);

	useEffect(() => {
		if (preserveSelectedTab) {
			return;
		}

		if (selectedWorkOrder && requestedWorkOrders.findIndex(wo => wo.WorkOrderId === selectedWorkOrder.WorkOrderId) >= 0) {
			setSelectedTab('requested');
		} else if (selectedWorkOrder && todoWorkOrders.findIndex(wo => wo.WorkOrderId === selectedWorkOrder.WorkOrderId) >= 0) {
			setSelectedTab('todo');
		} else if (selectedWorkOrder && doneWorkOrders.findIndex(wo => wo.WorkOrderId === selectedWorkOrder.WorkOrderId) >= 0) {
			setSelectedTab('done');
		} else if (selectedWorkOrder && archivedWorkOrders.findIndex(wo => wo.WorkOrderId === selectedWorkOrder.WorkOrderId) >= 0) {
			setSelectedTab('archived');
		} else if (selectedWorkOrder && deletedWorkOrders.findIndex(wo => wo.WorkOrderId === selectedWorkOrder.WorkOrderId) >= 0) {
			setSelectedTab('deleted');
		} else {
			if ((requestedWorkOrders.length > 0 && !isNewWorkOrder) || isNewRequest) {
				setSelectedTab('requested');
			} else if (todoWorkOrders.length > 0 || isNewWorkOrder) {
				setSelectedTab('todo');
			} else if (doneWorkOrders.length > 0) {
				setSelectedTab('done');
			} else if (archivedWorkOrders.length > 0) {
				setSelectedTab('archived');
			}
		}
	}, [selectedWorkOrder, workOrders, statusFilter]);
	// useEffect(() => {
	// 	if (workOrderIdFromParams) {
	// 		const workOrder = workOrders?.find((wo: ListWorkOrderDto) => wo.WorkOrderId === workOrderIdFromParams);
	// 		if (workOrder) {
	// 			setSelectedWorkOrder?.(workOrder);
	// 		}
	// 	} else if (workOrders && workOrders.length > 0) {
	// 		setSelectedWorkOrder?.(workOrders[0]);
	// 	}
	// }, [workOrderIdFromParams, workOrders]);

	if (isLoading) return <Paper withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 195px)', minHeight: isMobile ? 'calc(100vh - 225px)' : 'calc(100vh - 195px)' }} >
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>

	if (!workOrders || workOrders?.length === 0)
		return <Paper withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 195px)', minHeight: isMobile ? 'calc(100vh - 225px)' : 'calc(100vh - 195px)' }} >
				<Stack align="center" justify="center" mt="xl" px="sm">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconClipboardList size={60} />
					</ThemeIcon>
					<Text style={{ textAlign: 'center' }} lineClamp={1} c="dimmed">No work orders have been created yet.</Text>
				</Stack>
			</ScrollArea>
		</Paper>;

	const handleTabSelected = (value: string | null) => {
		setPreserveSelectedTab(false);
		setSelectedTab(value);
	}

	const handleWorkOrderSelect = (workOrder: ListWorkOrderDto): void => {
		setSelectedWorkOrder?.(workOrder);
	}

	const showCompletedWorkOrders = () => {
		setSelectedTab('done');
	}

	const showArchivedWorkOrders = () => {
		setSelectedTab('archived');
	}

	const showDeletedWorkOrders = () => {
		setSelectedTab('deleted');
	}

	const handleMarkWorkOrdersAsRead = (view: string) => {
		if (view == 'requested') {
			markAllAsRead({ Status: WorkOrderStatus.Requested });
		} else if (view == 'todo') {
			markAllAsRead({ Status: WorkOrderStatus.Open });
		} else if (view == 'done') {
			markAllAsRead({ Status: WorkOrderStatus.Completed });
		} else if (view == 'archived') {
			markAllAsRead({ Status: WorkOrderStatus.Archived });
		}

		setPreserveSelectedTab(true);
	}

	const MarkAllReadButton = (view: string) => {
		const list: ListWorkOrderDto[] = [];
		if (view == 'requested') {
			list.push(...requestedWorkOrders);
		} else if (view == 'todo') {
			list.push(...todoWorkOrders);
		} else if (view == 'done') {
			list.push(...doneWorkOrders);
		} else if (view == 'archived') {
			list.push(...archivedWorkOrders);
		}

		if (list.filter(wo => !wo.Read).length > 0) {
			return <Tooltip label="Mark all as read" color="var(--mantine-primary-color-filled)" withArrow arrowSize={10} radius="sm">
				<ActionIcon mr="sm" title="Mark all as read" variant="light" size="md" radius="md" loading={processingMarkAllAsRead} onClick={() => handleMarkWorkOrdersAsRead(view)}>
					<IconMailOpened size={18} />
				</ActionIcon>
			</Tooltip>;
		}

		return null;
	}

	return <>
		<Paper withBorder p={0} radius="md" style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 195px)', minHeight: isMobile ? 'calc(100vh - 225px)' : 'calc(100vh - 195px)' }} >
				<Tabs value={selectedTab} onChange={(value) => handleTabSelected(value)} defaultValue="todo" radius="md" className={classes.tabs}>
					<Tabs.List grow>
						<Tabs.Tab value="requested" leftSection={requestedWorkOrders.filter(wo => !wo.Read).length > 0 ? <ThemeIcon variant="filled" size={5} radius="xl" style={{ marginTop: 4 }}></ThemeIcon> : null}>Requested</Tabs.Tab>
						<Tabs.Tab value="todo" leftSection={todoWorkOrders.filter(wo => !wo.Read).length > 0 ? <ThemeIcon variant="filled" size={5} radius="xl" style={{ marginTop: 4 }}></ThemeIcon> : null}>To Do</Tabs.Tab>
						<Tabs.Tab value={selectedTab === "deleted" ? "deleted" : selectedTab === "archived" ? "archived" : "done"} leftSection={doneWorkOrders.filter(wo => !wo.Read).length > 0 || archivedWorkOrders.filter(wo => !wo.Read).length > 0 ? <ThemeIcon variant="filled" size={5} radius="xl" style={{ marginTop: 4 }}></ThemeIcon> : null}>
							<Group gap={10} wrap="nowrap">
								{(selectedTab === "done" || selectedTab == "requested" || selectedTab === "todo") && <Text inherit>Completed</Text>}
								{selectedTab === "archived" && <Text inherit>Archived</Text>}
								{selectedTab === "deleted" && <Text inherit>Deleted</Text>}
								<Menu position="bottom-end" shadow="md" radius="lg">
									<Menu.Target>
										<ActionIcon component="div" radius="sm" p={0} m={-2} variant="light" size={16} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}><IconChevronDown size={14} /></ActionIcon>
									</Menu.Target>
									<Menu.Dropdown>
										{selectedTab !== "done" &&
										<Menu.Item onClick={(e) => { e.preventDefault(); e.stopPropagation(); showCompletedWorkOrders(); }}>Completed Work Orders</Menu.Item>
										}
										{selectedTab !== "archived" &&
										<Menu.Item onClick={(e) => { e.preventDefault(); e.stopPropagation(); showArchivedWorkOrders(); }}>Archived Work Orders</Menu.Item>
										}
										{selectedTab !== "deleted" &&
										<Menu.Item onClick={(e) => { e.preventDefault(); e.stopPropagation(); showDeletedWorkOrders(); }}>Deleted Work Orders</Menu.Item>
										}
									</Menu.Dropdown>
								</Menu>
							</Group>
						</Tabs.Tab>
					</Tabs.List>
					<Tabs.Panel value="requested">
						<Group justify="space-between" w="100%" gap={0} wrap="nowrap" className={classes.sortBar}>
							{RequestedSortOptionsDropdown}
							{MarkAllReadButton('requested')}
						</Group>
						{requestedWorkOrders?.map((workOrder: ListWorkOrderDto) => (
							<WorkOrderListItem
								key={workOrder.WorkOrderId}
								workOrder={workOrder}
								selected={selectedWorkOrder?.WorkOrderId === workOrder.WorkOrderId}
								onSelect={handleWorkOrderSelect}
							/>
						))}
						{filterNoResultsRequested && <NoFilterResults filterType="work orders" resetFilters={resetFilters} />}
						{searchNoResultsRequested && <NoSearchResults searchType="work orders" query={query} setQuery={setQuery} />}
						{noDataRequested && <NoDataResults dataType="work orders" />}
					</Tabs.Panel>
					<Tabs.Panel value="todo">
						<Group justify="space-between" w="100%" gap={0} wrap="nowrap" className={classes.sortBar}>
							{ToDoSortOptionsDropdown}
							{MarkAllReadButton('todo')}
						</Group>
						{todoWorkOrders?.map((workOrder: ListWorkOrderDto) => (
							<WorkOrderListItem
								key={workOrder.WorkOrderId}
								workOrder={workOrder}
								selected={selectedWorkOrder?.WorkOrderId === workOrder.WorkOrderId}
								onSelect={handleWorkOrderSelect}
							/>
						))}
						{filterNoResultsTodo && <NoFilterResults filterType="work orders" resetFilters={resetFilters} />}
						{searchNoResultsTodo && <NoSearchResults searchType="work orders" query={query} setQuery={setQuery} />}
						{noDataTodo && <NoDataResults dataType="work orders" />}
					</Tabs.Panel>
					<Tabs.Panel value="done">
						<Group justify="space-between" w="100%" gap={0} wrap="nowrap" className={classes.sortBar}>
							{DoneSortOptionsDropdown}
							{MarkAllReadButton('done')}
						</Group>
						{doneWorkOrders?.map((workOrder: ListWorkOrderDto) => (
							<WorkOrderListItem
								key={workOrder.WorkOrderId}
								workOrder={workOrder}
								selected={selectedWorkOrder?.WorkOrderId === workOrder.WorkOrderId}
								onSelect={handleWorkOrderSelect}
							/>
						))}
						{filterNoResultsDone && <NoFilterResults filterType="work orders" resetFilters={resetFilters} />}
						{searchNoResultsDone && <NoSearchResults searchType="work orders" query={query} setQuery={setQuery} />}
						{noDataDone && <NoDataResults dataType="work orders" />}
					</Tabs.Panel>
					<Tabs.Panel value="archived">
						<Group justify="space-between" w="100%" gap={0} wrap="nowrap" className={classes.sortBar}>
							{ArchivedSortOptionsDropdown}
							{MarkAllReadButton('archived')}
						</Group>
						{archivedWorkOrders?.map((workOrder: ListWorkOrderDto) => (
							<WorkOrderListItem
								key={workOrder.WorkOrderId}
								workOrder={workOrder}
								selected={selectedWorkOrder?.WorkOrderId === workOrder.WorkOrderId}
								onSelect={handleWorkOrderSelect}
							/>
						))}
						{filterNoResultsArchived && <NoFilterResults filterType="work orders" resetFilters={resetFilters} />}
						{searchNoResultsArchived && <NoSearchResults searchType="work orders" query={query} setQuery={setQuery} />}
						{noDataArchived && <NoDataResults dataType="work orders" />}
					</Tabs.Panel>
					<Tabs.Panel value="deleted">
						<Group justify="space-between" w="100%" gap={0} wrap="nowrap" className={classes.sortBar}>
							{DeletedSortOptionsDropdown}
						</Group>
						{deletedWorkOrders?.map((workOrder: ListWorkOrderDto) => (
							<WorkOrderListItem
								key={workOrder.WorkOrderId}
								workOrder={workOrder}
								selected={selectedWorkOrder?.WorkOrderId === workOrder.WorkOrderId}
								onSelect={handleWorkOrderSelect}
							/>
						))}
						{filterNoResultsDeleted && <NoFilterResults filterType="work orders" resetFilters={resetFilters} />}
						{searchNoResultsDeleted && <NoSearchResults searchType="work orders" query={query} setQuery={setQuery} />}
						{noDataDeleted && <NoDataResults dataType="work orders" />}
					</Tabs.Panel>
				</Tabs>
			</ScrollArea>
		</Paper>
	</>
}
